import { createScrollIntentController } from '@/js/ly-core/core/scroll-intent'

if (!customElements.get('ly-main-product-media-thumbnailer')) {
    customElements.define('ly-main-product-media-thumbnailer', class extends LyPartScrollCarousel {
        #media = window.matchMedia('(max-width: 767px)')
        #mediaScope = null
        #selectedIndex = 0
        #ensureVisibleFrame = null
        #hasModelThumbnail = false
        #popoverV4VisibilityChangeUnsubscriber = null
        #popoverV5VisibilityChangeUnsubscriber = null
        #scrollIntentController = null
        #visibilityState = 'expanded'
        #visualTransitionTimer = null
        #visualFrame = null

        connectedCallback() {
            super.connectedCallback()

            this.#mediaScope = this.closest('.ly-main-product-media-wrapper')
            this.#hasModelThumbnail = this._items.some(item => item.classList.contains('model'))

            if (this.#hasModelThumbnail && this.#mediaScope) {
                this.#scrollIntentController = createScrollIntentController({
                    target: window,
                    threshold: 12,
                    shouldIgnoreEvent: this.#shouldIgnoreScrollIntent,
                    onDirectionChange: this.handleScrollIntent
                })

                this.#popoverV4VisibilityChangeUnsubscriber = window.subscribe(
                    'bus:popover_v4_visibility_changed',
                    this.handlePopoverVisibilityChange
                )

                this.#popoverV5VisibilityChangeUnsubscriber = window.subscribe(
                    'bus:popover_v5_visibility_changed',
                    this.handlePopoverVisibilityChange
                )
            }

            this.previewIndexChangeUnsubscriber = window.subscribe(
                'bus:main_product_media_previewer_index_changed',
                (payload) => {
                    const index = typeof payload === 'object' ? payload?.index : payload
                    const scope = typeof payload === 'object' ? payload?.scope : null

                    if (scope && scope !== this.#mediaScope) return
                    this.#selectIndex(index, { ensureVisible: true })
                }
            )

            this.removeItemClickEvents = this.addItemClickEvents()

            this.handleMatchMedia()
            this.#media.addEventListener('change', this.handleMatchMedia)
        }

        disconnectedCallback() {
            this.previewIndexChangeUnsubscriber?.()
            this.removeItemClickEvents?.()
            this.#popoverV4VisibilityChangeUnsubscriber?.()
            this.#popoverV4VisibilityChangeUnsubscriber = null
            this.#popoverV5VisibilityChangeUnsubscriber?.()
            this.#popoverV5VisibilityChangeUnsubscriber = null

            this.#media.removeEventListener('change', this.handleMatchMedia)

            cancelAnimationFrame(this.#ensureVisibleFrame)
            this.#ensureVisibleFrame = null
            this.#resetThumbnailVisibility()
            this.#scrollIntentController?.stop()
            this.#scrollIntentController = null
            this.#hasModelThumbnail = false
            this.#mediaScope = null

            super.disconnectedCallback()
        }

        addItemClickEvents = () => {
            const removeHandlers = []

            this._items.forEach((item, index) => {
                const handler = () => this.#selectIndex(index, {
                    ensureVisible: true,
                    publish: true
                })

                item.addEventListener('click', handler)
                removeHandlers.push(() => item.removeEventListener('click', handler))
            })

            return () => removeHandlers.forEach(handler => handler())
        }

        // 缩略图条的滚动位置不代表媒体选中状态，不能反向驱动主预览。
        _onIndexChanged = () => { }

        _updateActiveState = () => {
            this._items.forEach((item, index) => {
                item.classList.toggle('is-active', index === this.#selectedIndex)
            })
        }

        // ResizeObserver 只保证选中项可见，不再把它强制对齐到缩略图条左侧。
        _onResizeCallback = () => {
            const firstItem = this._items[0]
            if (!firstItem) return

            this._updateItemWidth(firstItem)
            this.#scheduleEnsureSelectedVisible('auto')
        }

        #selectIndex = (value, { ensureVisible = false, publish = false } = {}) => {
            const index = Number(value)
            if (!Number.isFinite(index)) return

            const nextIndex = Math.min(this._maxIndex, Math.max(0, Math.trunc(index)))
            const changed = nextIndex !== this.#selectedIndex

            this.#selectedIndex = nextIndex

            if (changed) {
                this._updateActiveState()
            }

            if (ensureVisible) {
                this.#scheduleEnsureSelectedVisible('smooth')
            }

            // 点击当前项也需要发出请求，用来修复极端情况下两边状态不一致的问题。
            if (publish) {
                window.publish('bus:main_product_media_thumbnailer_index_changed', {
                    index: nextIndex,
                    scope: this.#mediaScope
                })
            }
        }

        #scheduleEnsureSelectedVisible = (behavior) => {
            cancelAnimationFrame(this.#ensureVisibleFrame)

            this.#ensureVisibleFrame = requestAnimationFrame(() => {
                this.#ensureVisibleFrame = null
                this.#ensureSelectedVisible(behavior)
            })
        }

        #ensureSelectedVisible = (behavior) => {
            const item = this._items[this.#selectedIndex]
            if (!item) return

            const scrollerRect = this._scroller.getBoundingClientRect()
            const itemRect = item.getBoundingClientRect()
            let delta = 0

            if (itemRect.left < scrollerRect.left) {
                delta = itemRect.left - scrollerRect.left
            } else if (itemRect.right > scrollerRect.right) {
                delta = itemRect.right - scrollerRect.right
            }

            if (Math.abs(delta) < 1) return

            this._scroller.scrollTo({
                left: this._scroller.scrollLeft + delta,
                behavior
            })
        }

        handleMatchMedia = () => {
            if (!this.#hasModelThumbnail || !this.#scrollIntentController) {
                this.#resetThumbnailVisibility()
                this.#scrollIntentController?.stop()
                return
            }

            if (!this.#media.matches) {
                window.publish('bus:main_product_model_viewer_page_top_changed', {
                    active: false,
                    scope: this.#mediaScope
                })
                this.#resetThumbnailVisibility()
                this.#scrollIntentController?.stop()
                return
            }

            if (this.#hasOpenPopover()) {
                this.#scrollIntentController?.stop()
                return
            }

            this.#scrollIntentController?.start()
            this.#scrollIntentController?.reset()
        }

        handlePopoverVisibilityChange = ({ visible } = {}) => {
            if (!this.#scrollIntentController) return

            if (visible || this.#hasOpenPopover()) {
                this.#scrollIntentController.stop()
                return
            }

            this.handleMatchMedia()
        }

        handleScrollIntent = ({ direction }) => {
            if (!this.#media.matches) return
            if (!this.#isMediaAreaVisible()) return

            const shouldHide = direction === 'down'

            // 只有移动端向下滚动时才关闭图片预览。
            if (shouldHide) {
                window.publish('bus:main_product_media_preview_close_requested', {
                    scope: this.#mediaScope
                })
            }

            window.publish('bus:main_product_model_viewer_page_top_changed', {
                active: shouldHide,
                scope: this.#mediaScope
            })

            this.#setThumbnailHidden(shouldHide)
        }

        #setThumbnailHidden = (shouldHide) => {
            if (shouldHide) {
                this.#hideThumbnail()
                return
            }

            this.#showThumbnail()
        }

        #shouldIgnoreScrollIntent = (event) => {
            if (!(event.target instanceof Element)) return false

            return Boolean(event.target.closest('canvas, button, input, select, textarea, a, [contenteditable="true"]'))
        }

        #hasOpenPopover = () => Boolean(document.querySelector('ly-popover-v4.is-open'))

        #isMediaAreaVisible = () => {
            if (!this.#mediaScope) return false

            const rect = this.#mediaScope.getBoundingClientRect()
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight

            return rect.bottom > 0 && rect.top < viewportHeight
        }

        #hideThumbnail = () => {
            if (this.#visibilityState === 'hiding' || this.#visibilityState === 'collapsed') return

            this.#clearVisualTransition()
            this.#cancelVisualFrame()
            this.#visibilityState = 'hiding'
            this.classList.add('is-hidden')

            // 先完成合成层动画，再一次性收起布局，避免滚动期间逐帧重排。
            this.#visualTransitionTimer = setTimeout(this.#collapseThumbnailLayout, 180)
        }

        #showThumbnail = () => {
            if (this.#visibilityState === 'expanded' || this.#visibilityState === 'showing') return

            this.#clearVisualTransition()
            this.#cancelVisualFrame()

            if (this.#visibilityState === 'hiding') {
                this.#visibilityState = 'showing'
                this.classList.remove('is-hidden')
                this.#visualTransitionTimer = setTimeout(this.#finishShowingThumbnail, 180)
                return
            }

            this.#visibilityState = 'showing'
            this.#toggleCollapsedLayout(false)

            // 保留一帧隐藏状态，让恢复高度和进入动画分开提交。
            this.#visualFrame = requestAnimationFrame(() => {
                this.#visualFrame = null
                if (this.#visibilityState !== 'showing') return
                this.classList.remove('is-hidden')
            })
            this.#visualTransitionTimer = setTimeout(this.#finishShowingThumbnail, 200)
        }

        #collapseThumbnailLayout = () => {
            this.#visualTransitionTimer = null
            if (this.#visibilityState !== 'hiding') return

            this.#visibilityState = 'collapsed'
            this.#toggleCollapsedLayout(true)
        }

        #toggleCollapsedLayout = (collapsed) => {
            this.classList.toggle('is-collapsed', collapsed)
        }

        #finishShowingThumbnail = () => {
            this.#visualTransitionTimer = null
            if (this.#visibilityState !== 'showing') return

            this.#visibilityState = 'expanded'
        }

        #clearVisualTransition = () => {
            clearTimeout(this.#visualTransitionTimer)
            this.#visualTransitionTimer = null
        }

        #cancelVisualFrame = () => {
            cancelAnimationFrame(this.#visualFrame)
            this.#visualFrame = null
        }

        #resetThumbnailVisibility = () => {
            this.#clearVisualTransition()
            this.#cancelVisualFrame()
            this.#visibilityState = 'expanded'
            this.classList.remove('is-hidden', 'is-collapsed')
        }
    })
}
